import { useTranslation } from "react-i18next";
import type { ManagedContentRecord } from "@tellescope/types-models";
import { ArticleBlockRenderer } from "../../../components/ArticleBlockRenderer";

interface ContentViewerProps {
  content: ManagedContentRecord;
  attachmentUrl: string | null;
}

export const ContentViewer = ({ content, attachmentUrl }: ContentViewerProps) => {
  const { t } = useTranslation("content");

  // Article - display blocks array
  if (content.type === "Article") {
    if (content.blocks && content.blocks.length > 0) {
      return <ArticleBlockRenderer blocks={content.blocks} />;
    }

    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        <p>{t("No content available")}</p>
      </div>
    );
  }

  // PDF - display in iframe using PDF preview service
  if (content.type === "PDF") {
    if (!attachmentUrl) {
      return (
        <div className="flex items-center justify-center h-64 text-muted-foreground">
          <p>{t("PDF not available")}</p>
        </div>
      );
    }

    const pdfPreviewUrl = encodeURI(`https://pdf.tellescope.com?url=${attachmentUrl}`);
    return (
      <iframe
        src={pdfPreviewUrl}
        title={content.title}
        className="w-full h-[80vh] rounded-lg"
        style={{ minHeight: "500px" }}
      />
    );
  }

  // Video - display in video player
  if (content.type === "Video") {
    if (!attachmentUrl) {
      return (
        <div className="flex items-center justify-center h-64 text-muted-foreground">
          <p>{t("Video not available")}</p>
        </div>
      );
    }

    return (
      <div className="aspect-video w-full">
        <video src={attachmentUrl} controls className="w-full h-full rounded-lg" title={content.title}>
          <track kind="captions" />
        </video>
      </div>
    );
  }

  // Fallback for unknown types
  return (
    <div className="flex items-center justify-center h-64 text-muted-foreground">
      <p>{t("Preview not available for this content type")}</p>
    </div>
  );
};
